'use client';

import React from 'react';
import PropTypes from 'prop-types';
import styled, {css, keyframes} from 'styled-components';
import sizes, {SizesType, validSizeValues} from './lib/sizes';
import tiers, {TiersType, validTiersValue} from './lib/tiers';
import {getStrokeColorFromTier, getStrokeDashArrayFromTier} from './lib/utils';

export interface ProductBadgeProps {
  size: SizesType;
  tier: TiersType;
  animate?: boolean;
  [key: string]: unknown; // other props
}

const growEasing = 'cubic-bezier(1, 0.1, 0.5, 0.8)';
const filUpEasing = 'cubic-bezier(1, 0.1, 0.5, 0.9)';

const fillUp = keyframes`
  0% {
    stroke-dasharray: 0 100; 
  }
  
  50% {
    stroke-dasharray: 0 100; 
  }
`;

const grow = keyframes`
  0% {
    transform: scale(0.1);
  }
  100% { 
    transform: scale(1); 
  }
`;

const Svg = styled.svg`
  ${({animate}: {animate?: boolean}) =>
    animate &&
    css`
      animation: ${grow} 1.8s ${growEasing} forwards;
    `} @media (prefers-reduced-motion: reduce) {
    animation: none !important;
  }
`;

const TierCircle = styled.circle`
  ${({animate}: {animate?: boolean}) =>
    animate &&
    css`
      animation: ${fillUp} 2s ${filUpEasing} forwards;
    `} @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const ProductBadge: React.FC<ProductBadgeProps> = props => {
  const {size, tier, animate, ...otherProps} = props;

  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const invariant = require('invariant');
    invariant(tiers.includes(tier), "@nib-components/product-badge: `tier` invalid. It must be one of: 'basic', 'basic-plus', 'bronze', 'bronze-plus', 'silver', 'silver-plus', 'gold'");
  }

  return (
    <Svg animate={animate} viewBox="0 0 41.83098861837908 41.83098861837908" width={sizes[size]} height={sizes[size]} aria-labelledby="title" {...otherProps}>
      <title>{tier}</title>
      <circle cx="20.91549430918954" cy="20.91549430918954" r="15.91549430918954" fill="transparent" stroke="#e4e4e4" strokeWidth="10" />
      <TierCircle
        animate={animate}
        cx="20.91549430918954"
        cy="20.91549430918954"
        r="15.91549430918954"
        fill="transparent"
        stroke={getStrokeColorFromTier(tier)}
        strokeWidth="10"
        strokeDasharray={getStrokeDashArrayFromTier(tier)}
        strokeDashoffset="25"
      />
    </Svg>
  );
};

ProductBadge.displayName = 'ProductBadge';

ProductBadge.propTypes = {
  size: PropTypes.oneOf(validSizeValues).isRequired,
  tier: PropTypes.oneOf(validTiersValue).isRequired,
  animate: PropTypes.bool
};

ProductBadge.defaultProps = {
  animate: true
};

export default ProductBadge;
